import {
  AscFindTheWordsPuzzleBoardSize,
  AscFindTheWordsPuzzleCellType,
  AscFindTheWordsPuzzleWord,
} from "@ascendnow/shared";

/**
 * Generates a random character
 * @returns
 */
function generateRandomCharacter() {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const randomIndex = Math.floor(Math.random() * alphabet.length);
  return alphabet.charAt(randomIndex);
}

/**
 * Checks whether the given words will fit within the puzzle of provided size.
 * It is important to note that the function makes an approximate guess and the results will vary depending the words provided.
 * @param size
 * @param words
 * @returns boolean
 */
export function checkPuzzle(size: number, words: string[]) {
  return (
    words?.reduce((totalCount, word) => totalCount + word?.length, 0) <=
    size * size * 0.9
  );
}

/**
 * Generates a puzzle with "size" x "size" dimensions and with words randomly placed left-to-right, right-to-left, up-to-down and down-to-up.
 * The function attempts to create a balance between vertically and horizontally oriented words to create a more interesting puzzle.
 * The puzzle tries to place each word at most "size" x "size" times, if their is no suitable place found for a word within this tries the word will be skipped.
 * @param size
 * @param words
 * @returns cells: containing the placed words and random filler characters and placedWords: the list of words placed along with some metadata
 */
export function generatePuzzle(size: number, words: string[]) {
  if (!checkPuzzle(size, words)) {
    console.error(
      `Words can't fit in puzzle, please try selecting a bigger puzzle size.`
    );
  }

  // length of the puzzle to be generated
  const length = size * size;
  const placedWords: AscFindTheWordsPuzzleWord[] = [];
  const notPlacedWords: string[] = [];

  let cells: AscFindTheWordsPuzzleCellType[] = new Array(length).fill({});
  const sortedWords = [...words].sort((a, b) => b.length - a.length);
  const MAX_TRIES_FOR_WORD = length;

  /**
   * Used to keep balance between horizontal and vertical orientations.
   * - Negative value indicates how much more horizontally oriented words there are as compared to vertical
   * - Positive value indicates how much more vertically oriented words there are as compared to horizontal
   */
  let orientationDisparity = 0;

  /**
   * Loops over sortedWords to place them in the puzzle
   */
  for (let wordIndex = 0; wordIndex < sortedWords.length; wordIndex++) {
    const wordToBePlaced: Required<
      Pick<AscFindTheWordsPuzzleWord, "word" | "wordIndex" | "length">
    > = {
      word: sortedWords[wordIndex]?.slice(0)?.replace(/ /g, ""),
      wordIndex: wordIndex,
      length: sortedWords[wordIndex].length,
    };

    // Check if current word can fit inside the puzzle, if not skip word
    if (!wordToBePlaced?.length || wordToBePlaced?.length < 2) {
      throw new Error(
        `Entered word is too short, word must contain at least 2 characters`
      );
    }

    // Check if current word can fit inside the puzzle, if not skip word
    if (!wordToBePlaced?.length || wordToBePlaced?.length > size) {
      throw new Error(
        `${wordToBePlaced?.word} is too long, word length must be less than or equal to ${size}`
      );
    }

    let isWordPlaced = false;
    let triesForWord = 0;

    while (!isWordPlaced && triesForWord < MAX_TRIES_FOR_WORD) {
      triesForWord++;
      /**
       * Determines whether the word will be placed vertically (true) or horizontally (false)
       * @type boolean
       */
      const orientation =
        orientationDisparity === 0
          ? !!Math.floor(Math.random() * 2)
          : orientationDisparity < 0;
      const startX = Math.floor(
        Math.random() * (size - (orientation ? 0 : wordToBePlaced.length))
      );
      const startY = Math.floor(
        Math.random() * (size - (orientation ? wordToBePlaced.length : 0))
      );
      const direction = !!Math.floor(Math.random() * 2);

      const res = placeWord({
        word: wordToBePlaced?.word,
        direction,
        orientation,
        startX,
        startY,
        size,
        cells,
        wordIndex,
      });
      isWordPlaced = res.isWordPlaced;
      if (isWordPlaced && res.cells) {
        cells = res.cells;
        orientationDisparity = orientation
          ? orientationDisparity + 1
          : orientationDisparity - 1;
        placedWords.push({
          ...wordToBePlaced,
          orientation: orientation ? "vertical" : "horizontal",
          beginningCellNumber: startY * size + startX,
          direction: direction ? "forward" : "reverse",
          color: randomTonedDownColor(),
        });
        break;
      }
    }
    if (triesForWord >= length) {
      console.error(
        `Could not place ${wordToBePlaced?.word}. Exceeded maximum tries (${length} times).`,
        {
          wordToBePlaced,
          cells,
          triesForWord,
        }
      );
      notPlacedWords?.push(wordToBePlaced?.word);
      wordIndex++;
    }
  }

  return {
    cells: cells?.map((cell, index) =>
      cell?.character
        ? { ...cell, row: Math.floor(index / size), column: index % size }
        : {
            id: index,
            character: generateRandomCharacter(),
            row: Math.floor(index / size),
            column: index % size,
            wordIndices: [],
          }
    ),
    placedWords,
    notPlacedWords,
  };
}

export function generatePuzzleWithTries(size: number, words: string[]) {
  const puzzles: ReturnType<typeof generatePuzzle>[] = [];
  const puzzleStats: ReturnType<typeof getPuzzleStats>[] = [];

  for (let i = 0; i < size; i++) {
    puzzles[i] = generatePuzzle(size, words);
    puzzleStats[i] = getPuzzleStats(puzzles[i]);
    if (
      i !== 0 &&
      puzzleStats[i]?.numberOfPlacedWords >=
        puzzleStats[0]?.numberOfPlacedWords &&
      puzzleStats[i]?.numberOfOverlaps >= puzzleStats[0]?.numberOfOverlaps
    ) {
      const puzzles0 = puzzles[0];
      puzzles[0] = puzzles[i];
      puzzles[i] = puzzles0;
      const puzzleStats0 = puzzleStats[0];
      puzzleStats[0] = puzzleStats[i];
      puzzleStats[i] = puzzleStats0;
    }
  }

  return puzzles[0];
}

function getPuzzleStats({
  cells,
  placedWords,
}: ReturnType<typeof generatePuzzle>) {
  const numberOfPlacedWords = placedWords?.length;
  const numberOfOverlaps = cells?.filter(
    (cell) => cell?.wordIndices?.length > 1
  )?.length;

  return {
    numberOfPlacedWords,
    numberOfOverlaps,
  };
}

function placeWord({
  word,
  direction,
  orientation,
  startX,
  startY,
  size,
  cells,
  wordIndex,
}: {
  word: string;
  orientation: boolean;
  direction: boolean;
  startX: number;
  startY: number;
  size: number;
  cells: AscFindTheWordsPuzzleCellType[];
  wordIndex: number;
}): {
  isWordPlaced: boolean;
  errorMessage?: string;
  cells?: AscFindTheWordsPuzzleCellType[];
} {
  const cellsClone = JSON.parse(JSON.stringify(cells));
  for (let i = 0; i < word.length; i++) {
    const index = calculateCharacterIndex({
      index: i,
      orientation,
      startX,
      startY,
      wordLength: word.length,
      size,
    });
    const wordCharAtIndex = direction ? i : word?.length - 1 - i;
    if (!index) {
      return {
        isWordPlaced: false,
        errorMessage: "Place doesn't fit.",
      };
    }

    if (
      cellsClone[index]?.character &&
      cellsClone[index]?.character !== word?.charAt(wordCharAtIndex)
    ) {
      return {
        isWordPlaced: false,
        errorMessage: `Conflicting character ${
          cellsClone[index]?.character
        } at ${index} from word with wordIndex: ${
          cellsClone[index]?.wordIndex
        }. \nCouldn't place ${word?.charAt(
          wordCharAtIndex
        )} from word with wordIndex: ${wordIndex}.`,
      };
    }
    cellsClone[index].id = index;
    cellsClone[index].character = word?.charAt(wordCharAtIndex);
    cellsClone[index].wordIndices = [
      ...(cellsClone[index].wordIndices || []),
      wordIndex,
    ];
    cellsClone[index].row = startY;
    cellsClone[index].column = startX;
  }

  return {
    isWordPlaced: true,
    cells: cellsClone,
  };
}

function calculateCharacterIndex({
  orientation,
  startX,
  startY,
  wordLength,
  size,
  index,
}: {
  orientation: boolean;
  startX: number;
  startY: number;
  wordLength: number;
  index: number;
  size: number;
}) {
  if (!orientation && startX + wordLength <= size) {
    return startY * size + startX + index;
  } else if (orientation && startY + wordLength <= size) {
    return (startY + index) * size + startX;
  }
}

export function randomTonedDownColor() {
  const hue = Math.floor(Math.random() * 36) * 10; // random hue value between 0 and 350 which is a multiple of 10
  const saturation = Math.floor(Math.random() * 30) + 60; // random saturation between 60 and 89
  const lightness = Math.floor(Math.random() * 20) + 50; // random lightness between 50 and 79
  const color = "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
  return color;
}

export function placeGeneratedPuzzle(
  size: AscFindTheWordsPuzzleBoardSize,
  words: AscFindTheWordsPuzzleWord[]
) {
  const cells: AscFindTheWordsPuzzleCellType[] = new Array(size * size)?.fill(
    null
  );
  words?.forEach((word) => {
    const _length = word?.length || 0;
    for (let i = 0; i < _length; i++) {
      const index =
        word?.orientation === "horizontal"
          ? word?.beginningCellNumber + i
          : word?.beginningCellNumber + i * size;
      cells[index] = {
        id: index,
        wordIndices: [...(cells[index]?.wordIndices || []), word?.wordIndex],
        character: word?.word?.charAt(
          word?.direction === "forward" ? i : word?.word?.length - i - 1
        ),
        row: Math.floor(index / size),
        column: index % size,
      };
    }
  });

  return cells?.map(
    (cell, index) =>
      cell || {
        id: index,
        character: generateRandomCharacter(),
        row: Math.floor(index / size),
        column: index % size,
        wordIndices: [],
      }
  );
}
